/**
 * Dependencies.
 */
import { BrowserMultiFormatReader } from '@zxing/library';
import { debounce, orderBy, uniqBy } from 'lodash-es';
import Vue from 'vue';
import { Component, Watch } from 'vue-property-decorator';

import { servicePointOrderPackageService } from '@/api/service-point/order-package/order-package.service';
import { ServicePointOrderModel } from '@/api/service-point/order/order.model';
import { servicePointService } from '@/api/service-point/service-point/service-point.service';
import orderState from '@/modules/service-point/constants/order.constant';

/**
 * Component.
 */
@Component({ name: 'receive-packages-scan-scene' })
export default class extends Vue {
  /**
   * Property `isLoadingOrders`.
   */
  orderState = orderState;

  /**
   * Property `isLoadingOrders`.
   */
  isLoadingOrders = false;

  /**
   * Property `isLoadingPackages`.
   */
  isLoadingPackages = false;

  /**
   * Property `isCameraLoading`.
   */
  isCameraLoading = true;

  /**
   * Property `canShowScanCodeModal`.
   */
  canShowScanCodeModal = false;

  /**
   * Property `orders`.
   */
  orders: ServicePointOrderModel[] = [];

  /**
   * Property `codeReader`.
   */
  codeReader = new BrowserMultiFormatReader();

  /**
   * Property `packageCode`.
   */
  packageCode = null;

  /**
   * Property `searchCode`.
   */
  searchCode = debounce(this.handleValidateCode, 1000);

  /**
   * Getter `receiveOrders`.
   */
  get receiveOrders() {
    let receiveOrders = [...this.orders];
    receiveOrders = orderBy(receiveOrders, ['statusDate'], ['desc']);

    return receiveOrders;
  }

  /**
   * Getter `packages`.
   */
  get packages() {
    const orders = [...this.orders];

    let packages = [];

    orders.forEach(
      ({ status: { code } = {}, packages: orderPackages = [] }) => {
        const { RECEIVE_PENDING, RECEIVED } = orderState;

        if (code === RECEIVE_PENDING || code === RECEIVED) {
          packages = [...packages, ...orderPackages];
        }
      }
    );

    packages = uniqBy(packages, 'id');
    packages = orderBy(packages, ['status.code'], ['desc']);

    return packages;
  }

  /**
   * Getter `isAllPackagesRead`.
   */
  get isAllPackagesRead() {
    let isAllRead = this.packages.length ? true : false;

    this.packages.forEach(({ status: { code } }) => {
      const { RECEIVE_PENDING } = orderState;

      if (code === RECEIVE_PENDING) {
        isAllRead = false;
      }
    });

    return isAllRead;
  }

  /**
   * Getter `totalUnreceivePackages`.
   */
  get totalUnreceivePackages() {
    let total = 0;

    this.packages.forEach(({ status: { code } }) => {
      const { RECEIVE_PENDING } = orderState;

      if (code === RECEIVE_PENDING) {
        total++;
      }
    });

    return total;
  }

  /**
   * Getter `notifySucessSettings`.
   */
  get notifySucessSettings() {
    return {
      backgroundColor: 'var(--pk-color-success-lite)',
      displayMode: 2,
      icon: 'material-icons',
      iconColor: 'var(--pk-color-success)',
      iconText: 'check_circle',
      id: 'receive-success',
      progressBarColor: 'var(--pk-color-success)',
      transitionIn: 'fadeIn',
      transitionInMobile: 'fadeIn',
      transitionOut: 'fadeOut',
      transitionOutMobile: 'fadeOut'
    } as unknown;
  }

  /**
   * Getter `notifyErrorSettings`.
   */
  get notifyErrorSettings() {
    return {
      backgroundColor: 'var(--pk-color-error-lite)',
      displayMode: 2,
      icon: 'material-icons',
      iconColor: 'var(--pk-color-error)',
      iconText: 'warning',
      id: 'receive-error',
      progressBarColor: 'var(--pk-color-error)',
      transitionIn: 'fadeIn',
      transitionInMobile: 'fadeIn',
      transitionOut: 'fadeOut',
      transitionOutMobile: 'fadeOut'
    } as unknown;
  }

  /**
   * Lifecycle created hook.
   */
  created() {
    this.handleInitialize();
  }

  /**
   * Lifecycle before destroy hook.
   */
  beforeDestroy() {
    if (this.codeReader) {
      this.codeReader.reset();
    }
  }

  /**
   * Handle initialize.
   */
  handleInitialize() {
    this.handleGetOrders();
  }

  /**
   * Handle copy package code.
   */
  handleCopyPackageCode(code) {
    this.packageCode = code;
    this.searchCode(code);
  }

  /**
   * Handle get orders.
   */
  async handleGetOrders() {
    this.isLoadingOrders = true;

    try {
      const { RECEIVE_PENDING } = orderState;
      this.orders =
        (await servicePointService.getOrdersByStatusCode(RECEIVE_PENDING)) ||
        [];
    } catch (error) {
      this.orders = [];

      if (process.env.BUILD_MODE !== 'prd') {
        console.error({
          error,
          method: 'handleGetOrders',
          scene: 'ReceivePackagesScanScene'
        });
      }
    } finally {
      this.isLoadingOrders = false;
      this.packageCode = null;
    }
  }

  /**
   * Handle set package received.
   */
  handleSetPackageReceived({ packageCode, trackingNumber }) {
    const { orders = [] } = this || {};
    const { RECEIVED } = orderState;

    const order = orders.find(
      ({ delivery: { trackingNumber: tracking } = {} }) =>
        tracking === trackingNumber
    );

    if (order) {
      const { packages = [] } = order || {};
      const box = packages.find(
        ({ deliveryPackage: { packageCode: code } = {} }) =>
          code === packageCode
      );

      if (box) {
        box.status.code = RECEIVED;
        this.packageCode = null;
      }
    }

    if (this.isAllPackagesRead) {
      this.handleHideScanCodeModal();
    }
  }

  /**
   * Handle hide scan code modal.
   */
  handleHideScanCodeModal() {
    this.canShowScanCodeModal = false;
  }

  /**
   * Handle show scan code modal.
   */
  handleShowScanCodeModal() {
    this.isCameraLoading = true;
    this.canShowScanCodeModal = true;

    const { canEnumerateDevices = false } = this.codeReader || {};

    if (canEnumerateDevices) {
      this.$nextTick().then(async () => {
        (this.$refs.codeScanner as HTMLVideoElement).oncanplay = () => {
          this.isCameraLoading = false;
        };

        this.handleStartVideoDecoding();
      });
    }
  }

  /**
   * Handle start video decoding.
   */
  async handleStartVideoDecoding() {
    try {
      if (this.codeReader) {
        const result = await this.codeReader.decodeOnceFromVideoDevice(
          undefined,
          this.$refs.codeScanner as HTMLVideoElement
        );

        if (result && result.getText()) {
          const code = result.getText();

          await this.handleValidateCode(code);

          this.codeReader.reset();
          this.handleStartVideoDecoding();
        }
      }
    } catch (error) {
      if (process.env.BUILD_MODE !== 'prd') {
        console.error({
          error,
          method: 'handleStartVideoDecoding',
          scene: 'ReceivePackagesScanScene'
        });
      }
    }
  }

  /**
   * Handle validate code.
   */
  async handleValidateCode(code: string) {
    const packageCode = code;

    if (packageCode) {
      try {
        this.isLoadingPackages = true;

        const response = await servicePointOrderPackageService.receive(
          packageCode
        );

        this.$kit.notify.show(
          this.i18n('SERVICE_POINT.LABELS.SUCCESS'),
          this.i18n('SERVICE_POINT.SUCCESS.CODE_READ', false, 'upperFirst', {
            code: packageCode
          }),
          this.notifySucessSettings
        );

        const { delivery: { trackingNumber = null } = {} } = response || {};

        this.handleSetPackageReceived({
          packageCode: code,
          trackingNumber
        });
      } catch (error) {
        this.$kit.notify.show(
          this.i18n('SERVICE_POINT.LABELS.ERROR'),
          this.i18n('SERVICE_POINT.ERRORS.VALIDATING_CODE'),
          this.notifyErrorSettings
        );

        if (process.env.BUILD_MODE !== 'prd') {
          console.error({
            error,
            method: 'handleValidateCode',
            scene: 'ReceivePackagesScanScene'
          });
        }
      } finally {
        this.isLoadingPackages = false;
      }
    }
  }

  /**
   * Handle close receive.
   */
  handleCloseReceive() {
    this.$router.push({
      name: 'ServicePointHome'
    });
  }

  /**
   * Handle change can show scan code modal.
   */
  @Watch('canShowScanCodeModal')
  handleChangeCanShowScanCodeModal() {
    if (this.canShowScanCodeModal === false && this.codeReader) {
      this.codeReader.reset();
    }
  }
}
